import * as _ from 'lodash';
import { FormEvent } from 'react';
import { IDonorProfileContact, IDonorProfileDonorData, IListItem, IState } from '@nkr/models/index';
import { DonationType } from '@nkr/enums/index';
import { updateProfileDonorData } from './update-profile-donor-data';
import { Col, ControlLabel, Form, FormControl, FormGroup, Row } from 'react-bootstrap';
import MaskedInput from 'react-maskedinput';
import YesNoUnknown from '../yes-no-unknown';
import Kits from './kits';
import IsWorkedUp from './is-worked-up';
import Alias from './alias';
import { IValidation } from './validation';

interface IProps {
    centerId: number,
    data: IDonorProfileDonorData,
    isSuperAdmin: boolean,
    isCenter: boolean,
    validation: IValidation,
    surgeonList: IDonorProfileContact[],
    coordinatorList: IDonorProfileContact[],
    countryList: IListItem[],
    stateList: IState[],
    declineAdpReasonList: IListItem[],
    relationshipToPatientList: IListItem[],
    remoteEvalStatus: IListItem[],
    rdnCenterList: IListItem[],
    onChange: (data: IDonorProfileDonorData) => void
}

const DonorDataContentSelfRegistered = (props: IProps) => {
    const handleSelectChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = parseInt(e.currentTarget.value);
        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onNumericChange(name, value, data);

        props.onChange(data);
    };

    const handleTextChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onTextChange(name, value, data);

        props.onChange(data);
    };

    const handleBooleanChange = (name: string, value: boolean) => {
        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onBooleanChange(name, value, data);

        props.onChange(data);
    };

    let canChangeAlias = props.data.canChangeAlias || props.isSuperAdmin;

    let showRdnOption = props.data.donationType == DonationType.StandardVoucher || props.data.donationType == DonationType.DirectedDonor;

    let rdnCenterList = props.rdnCenterList.filter(x => x.id != props.data.centerId);

    return (
        <div className='container-fluid donor-profile-section donor-profile-content'>
            {/* <span>self-registered</span> */}
            <Row>
                <Col xs={12} lg={6}>
                    <Form horizontal>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Donation Type:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>{props.data.donationTypeDescription}</div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Registration Date:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>{props.data.registrationDate}</div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Donor Surgeon:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='surgeonContactId'
                                    value={props.data.surgeonContactId}
                                    onChange={handleSelectChange}
                                >
                                    <option value={0}>- Select -</option>
                                    {props.surgeonList.map(surgeon =>
                                        <option key={'ds-' + surgeon.id} value={surgeon.id}>{surgeon.fullName}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>MRN:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    name='mrn'
                                    maxLength={24}
                                    className='form-control'
                                    value={props.data.mrn}
                                    onChange={handleTextChange} />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Hint/Cross Reference:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    name='crossReferenceHint'
                                    maxLength={32}
                                    className='form-control'
                                    value={props.data.crossReferenceHint}
                                    onChange={handleTextChange} />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Earliest donation date:</Col>
                            <Col xs={12} lg={7}>
                                <MaskedInput
                                    name='donationStartDate'
                                    className='form-control'
                                    mask='11/11/1111'
                                    placeholder='mm/dd/yyyy'
                                    value={props.data.donationStartDate}
                                    onChange={handleTextChange}
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Latest donation date:</Col>
                            <Col xs={12} lg={7}>
                                <MaskedInput
                                    name='donationEndDate'
                                    className='form-control'
                                    mask='11/11/1111'
                                    placeholder='mm/dd/yyyy'
                                    value={props.data.donationEndDate}
                                    onChange={handleTextChange}
                                />
                            </Col>
                        </FormGroup>

                        <IsWorkedUp
                            isWorkedUp={props.data.isWorkedUp}
                            onChange={handleBooleanChange} />

                        <Alias
                            alias={props.data.alias}
                            allowEdit={canChangeAlias}
                            isValid={props.validation.alias}
                            onChange={handleTextChange}
                        />

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>UNOS Donor ID:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    name='unosId'
                                    maxLength={7}
                                    value={props.data.unosId}
                                    onChange={handleTextChange} />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Primary Coordinator:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    componentClass='select'
                                    name='coordinatorId'
                                    value={props.data.coordinatorId}
                                    onChange={handleSelectChange}
                                >
                                    <option value={0}>Default Coordinator</option>
                                    {props.coordinatorList.map(coordinator =>
                                        <option key={'dc-' + coordinator.id} value={coordinator.id}>{coordinator.fullName}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        {props.data.isRemotePhlebo && (
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Use Traveling Phlebotomist:</Col>
                                <Col xs={12} lg={7}>
                                    <YesNoUnknown
                                        name='usePhlebotomy'
                                        yes={props.data.usePhlebotomy}
                                        no={!props.data.usePhlebotomy}
                                        onChange={handleBooleanChange} />
                                </Col>
                            </FormGroup>
                        )}

                        {showRdnOption && (
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>RDN Managing Center:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        componentClass='select'
                                        name='rdnCenterId'
                                        value={props.data.rdnCenterId}
                                        onChange={handleSelectChange}
                                    >
                                        <option value={0}>- Not a Remote Donor -</option>
                                        {rdnCenterList.map(item =>
                                            <option key={'rdn-ctr-' + item.id} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroup>
                        )}
                    </Form>

                    <br />

                    <Kits
                        directCryoSend={props.data.directCryoSend}
                        directXmSend={props.data.directXmSend}
                        directSerologySend={props.data.directSerologySend}
                        onBooleanChange={handleBooleanChange}
                    />
                </Col>
            </Row>
        </div>
    )
}

export default DonorDataContentSelfRegistered;